
import React, { useEffect, useState } from "react";
import "./style/addcart.css";
import "./style/customermodel.css";
import toast, { Toaster } from "react-hot-toast";
import "react-loading-skeleton/dist/skeleton.css";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { addToCart } from "../ProductDetail/service/productcart.service";
import { getCustomers } from "./service/Customers.service";
import { setCartCount } from "./redux/cartSlice";
import { getCartCount } from "../../Component/header/service/CartCount.service";
import { Link } from "react-router-dom";

import {
  addToWishlist,
  fetchWishlist,
} from "../Pages/wishlist/redux/wishlistSlice";

function AddCart({ sizes = [] }) {
  const dispatch = useDispatch();

  const cartCount = useSelector(
    (state) => Number(state.cart.cartCount) || 0
  );

  const [quantities, setQuantities] = useState({});
  const [loadingId, setLoadingId] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [activeIndex, setActiveIndex] = useState(-1);

  const [wishlistLoadingId, setWishlistLoadingId] = useState(null);

  const [activeCartButtons, setActiveCartButtons] = useState({});
  const [activeWishlistButtons, setActiveWishlistButtons] = useState({});

  // true ONLY when 10+ is selected
  const [customQty, setCustomQty] = useState({});

  const [updateLoadingId, setUpdateLoadingId] = useState(null);

  const [pendingQuantities, setPendingQuantities] = useState({});

  // =========================================================
  // GET CURRENT CUSTOMER
  // =========================================================

  const getCurrentCustomer = async () => {
    if (selectedCustomer) {
      const customerId =
        selectedCustomer.customerId ||
        selectedCustomer.customer_id ||
        selectedCustomer.id;

      if (customerId) {
        return {
          ...selectedCustomer,
          id: Number(customerId),
        };
      }
    }

    try {
      const cartRes = await getCartCount("fresh", 1, null);

      const cartData = cartRes?.data;

      if (cartData?.customer_id) {
        const customer = {
          id: Number(cartData.customer_id),
        };

        setSelectedCustomer(customer);

        return customer;
      }
    } catch (error) {
      console.error("Server customer check error:", error);
    }

    return null;
  };

  // =========================================================
  // OPEN CUSTOMER MODAL
  // =========================================================

  const openCustomerModalForProduct = (product) => {
    setSelectedProduct(product);
    setSearch("");
    setCustomers([]);
    setActiveIndex(-1);
    setShowModal(true);
  };

  // =========================================================
  // CUSTOMER SEARCH
  // =========================================================

  useEffect(() => {
    if (!showModal) return;

    const searchText = search.trim();

    const timer = setTimeout(async () => {
      try {
        const list = await getCustomers(searchText);

        setCustomers(Array.isArray(list) ? list : []);
        setActiveIndex(-1);
      } catch (error) {
        console.error("Customer search API error:", error);
        setCustomers([]);
      }
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [search, showModal]);

  // =========================================================
  // CUSTOM QUANTITY CHANGE
  // =========================================================

  const handleCustomQuantityChange = (item, value) => {
    const productId = item.product_id;

    // Only numbers
    if (!/^\d*$/.test(value)) {
      return;
    }

    setQuantities((prev) => ({
      ...prev,
      [productId]: value,
    }));

    if (value === "") {
      setPendingQuantities((prev) => ({
        ...prev,
        [productId]: "",
      }));

      return;
    }

    const qty = Number(value);
    const stock = Number(item.avail_stock || 0);

    if (qty > stock) {
      toast.error(`Only ${stock} stock available`);

      // Keep entered value
      setPendingQuantities((prev) => ({
        ...prev,
        [productId]: qty,
      }));

      return;
    }

    setPendingQuantities((prev) => ({
      ...prev,
      [productId]: qty,
    }));
  };

  // =========================================================
  // UPDATE CUSTOM QUANTITY
  // =========================================================

  const handleUpdateQuantity = async (item) => {
    const productId = item.product_id;

    const qty = Number(
      pendingQuantities[productId] ?? quantities[productId]
    );

    const stock = Number(item.avail_stock || 0);

    if (!qty || qty <= 0) {
      toast.error("Please enter quantity");
      return;
    }

    if (qty > stock) {
      toast.error(`Only ${stock} stock available`);
      return;
    }

    setUpdateLoadingId(productId);

    try {
      const customer = await getCurrentCustomer();

      if (!customer) {
        await openCustomerModalForProduct(item);
        return;
      }

      const customerId =
        customer.customerId ||
        customer.customer_id ||
        customer.id;

      if (!customerId) {
        await openCustomerModalForProduct(item);
        return;
      }

      const payload = {
        cart_type: "fresh",
        customer_id: Number(customerId),
        product_listing_id:
          item.product_listing_id || item.product_id,
        qty: qty,
      };

      const res = await addToCart(payload);

      if (res?.success) {
        setSelectedCustomer({
          ...customer,
          id: Number(customerId),
        });

        setQuantities((prev) => ({
          ...prev,
          [productId]: qty,
        }));

        setPendingQuantities((prev) => {
          const updated = { ...prev };
          delete updated[productId];
          return updated;
        });

        // IMPORTANT:
        // Keep custom input open after update
        setCustomQty((prev) => ({
          ...prev,
          [productId]: true,
        }));

        setActiveCartButtons((prev) => ({
          ...prev,
          [productId]: true,
        }));

        toast.success(
          res?.message || "Cart updated successfully"
        );
      } else {
        toast.error(
          res?.message || "Failed to update cart"
        );
      }
    } catch (error) {
      console.error("Update quantity error:", error);

      toast.error(
        error?.response?.data?.message ||
          "Failed to update cart"
      );
    } finally {
      setUpdateLoadingId(null);
    }
  };

  // =========================================================
  // QUANTITY CHANGE 1-9
  // =========================================================

  const handleQuantityChange = async (product, quantity) => {
    const qty = Number(quantity);

    if (qty <= 0) return;

    // 10+ is handled separately
    if (qty >= 10) {
      return;
    }

    const stock = Number(product.avail_stock || 0);

    if (qty > stock) {
      toast.error(
        `Only ${product.avail_stock} stock available`
      );

      return;
    }

    // IMPORTANT:
    // If user selects 1-9, custom mode must remain false.
    setCustomQty((prev) => ({
      ...prev,
      [product.product_id]: false,
    }));

    setPendingQuantities((prev) => {
      const updated = { ...prev };
      delete updated[product.product_id];
      return updated;
    });

    const customer = await getCurrentCustomer();

    if (!customer) {
      await openCustomerModalForProduct(product);
      return;
    }

    await handleCustomerSelect(
      customer,
      product,
      qty
    );
  };

  // =========================================================
  // CART ICON CLICK
  // =========================================================

  const handleAddToCart = async (item) => {
    const productId = item.product_id;

    const qty = Number(
      pendingQuantities[productId] ??
        quantities[productId] ??
        0
    );

    if (qty <= 0) {
      toast.error("Please select quantity");
      return;
    }

    const stock = Number(item.avail_stock || 0);

    if (qty > stock) {
      toast.error(`Only ${stock} stock available`);
      return;
    }

    setLoadingId(productId);

    try {
      const customer = await getCurrentCustomer();

      if (customer) {
        await handleCustomerSelect(
          customer,
          item,
          qty
        );

        return;
      }

      await openCustomerModalForProduct(item);
    } catch (error) {
      console.error(error);

      toast.error("Unable to load customer");
    } finally {
      setLoadingId(null);
    }
  };

  // =========================================================
  // CUSTOMER SELECT
  // =========================================================

  const handleCustomerSelect = async (
    customer,
    product = selectedProduct,
    selectedQty = null
  ) => {
    if (!product) return;

    const productId = product.product_id;

    const customerId =
      customer?.customerId ||
      customer?.customer_id ||
      customer?.id;

    if (!customerId) {
      toast.error("Invalid customer");
      return;
    }

    const qty = Number(
      selectedQty ??
        pendingQuantities[productId] ??
        quantities[productId] ??
        0
    );

    if (qty <= 0) {
      toast.error("Please select quantity");
      return;
    }

    const stock = Number(product.avail_stock || 0);

    if (qty > stock) {
      toast.error(
        `Only ${product.avail_stock} stock available`
      );

      return;
    }

    setSelectedCustomer({
      ...customer,
      id: Number(customerId),
    });

    setLoadingId(productId);

    try {
      const payload = {
        cart_type: "fresh",
        customer_id: Number(customerId),
        product_listing_id:
          product.product_listing_id ||
          product.product_id,
        qty: qty,
      };

      const res = await addToCart(payload);

      if (res?.success) {
        dispatch(
          setCartCount(cartCount + qty)
        );

        setQuantities((prev) => ({
          ...prev,
          [productId]: qty,
        }));

        setPendingQuantities((prev) => {
          const updated = { ...prev };
          delete updated[productId];
          return updated;
        });

        // =================================================
        // IMPORTANT FIX
        //
        // DO NOT set customQty true here.
        //
        // Therefore:
        // 1-9 => dropdown remains dropdown
        // 10+ => input remains input
        // =================================================

        if (qty >= 10) {
          setCustomQty((prev) => ({
            ...prev,
            [productId]: true,
          }));
        } else {
          setCustomQty((prev) => ({
            ...prev,
            [productId]: false,
          }));
        }

        setActiveCartButtons((prev) => ({
          ...prev,
          [productId]: true,
        }));

        toast.success(
          res?.message || "Added to Cart"
        );

        setShowModal(false);
        setSelectedProduct(null);
        setSearch("");
        setCustomers([]);
        setActiveIndex(-1);
      } else {
        toast.error(
          res?.message || "Failed"
        );
      }
    } catch (error) {
      console.error("Add cart error:", error);

      toast.error(
        error?.response?.data?.message ||
          "Failed to add cart"
      );
    } finally {
      setLoadingId(null);
    }
  };

  // =========================================================
  // CUSTOMER RESULT
  // =========================================================

  const filteredCustomers = customers;

  // =========================================================
  // HIGHLIGHT SEARCH
  // =========================================================

  const highlightText = (
    text = "",
    search = ""
  ) => {
    const searchText = search.trim();

    if (searchText.length < 2) {
      return text;
    }

    const escapedSearch = searchText.replace(
      /[.*+?^${}()|[\]\\]/g,
      "\\$&"
    );

    const regex = new RegExp(
      `(${escapedSearch})`,
      "gi"
    );

    return String(text)
      .split(regex)
      .map((part, index) =>
        part.toLowerCase() ===
        searchText.toLowerCase() ? (
          <span
            key={index}
            style={{
              fontWeight: "700",
              color: "#000",
            }}
          >
            {part}
          </span>
        ) : (
          part
        )
      );
  };

  // =========================================================
  // WISHLIST
  // =========================================================

  const handleAddWishlist = async (item) => {
    if (wishlistLoadingId) return;

    setWishlistLoadingId(item.product_id);

    try {
      const customer = await getCurrentCustomer();

      if (!customer) {
        toast.error("Please select customer first");
        return;
      }

      const customerId =
        customer.customerId ||
        customer.customer_id ||
        customer.id;

      if (!customerId) {
        toast.error("Please select customer first");
        return;
      }

      const payload = {
        user_id: 1,
        customer_id: Number(customerId),
        product_listing_id:
          item.product_listing_id ||
          item.product_id,
      };

      const res = await dispatch(
        addToWishlist(payload)
      ).unwrap();

      if (res?.success !== false) {
        setActiveWishlistButtons((prev) => ({
          ...prev,
          [item.product_id]: true,
        }));

        toast.success("Added to Wishlist");

        dispatch(
          fetchWishlist({
            userId: payload.user_id,
            customerId: payload.customer_id,
          })
        );
      } else {
        toast.error(
          res?.message || "Failed"
        );
      }
    } catch (err) {
      console.error("Wishlist error:", err);

      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Something went wrong"
      );
    } finally {
      setWishlistLoadingId(null);
    }
  };

  // =========================================================
  // UI
  // =========================================================

  return (
    <>
      <Toaster position="top-right" />

      <div
        className="TableBoxcart"
        style={{ marginTop: 0 }}
      >
        <div className="table-responsive">
          <div className="table-box-content">

            {/* ================= HEADER ================= */}

            <div className="table-header-fixed">
              <table className="table table-sm mb-0">
                <thead className="text-center">
                  <tr>
                    <th>Size</th>
                    <th>Shape</th>
                    <th>MRP (₹)</th>
                    <th>Stock</th>
                    <th>Ord. Qty</th>
                    <th>Cart</th>
                    <th>Wishlist</th>
                  </tr>
                </thead>
              </table>
            </div>

            {/* ================= BODY ================= */}

            <div className="table-body-scroll">
              <table className="table table-sm mb-0">
                <tbody>

                  {sizes.length > 0 ? (
                    sizes.map((item) => (
                      <tr
                        key={item.product_id}
                      >

                        {/* ================= SIZE ================= */}

                        <td>
                          <Link
                            to="#"
                            className="size-tooltip"
                            onMouseEnter={(e) => {
                              const tooltip =
                                e.currentTarget.querySelector(
                                  ".tooltip-barcode"
                                );

                              const rect =
                                e.currentTarget.getBoundingClientRect();

                              if (tooltip) {
                                tooltip.style.left = `${
                                  rect.left +
                                  rect.width / 2
                                }px`;

                                tooltip.style.top = `${
                                  rect.top - 8
                                }px`;
                              }
                            }}
                            onClick={(e) =>
                              e.preventDefault()
                            }
                          >
                            {item.size}

                            <span className="tooltip-barcode">
                              {item.barcode}
                            </span>
                          </Link>
                        </td>

                        {/* ================= SHAPE ================= */}

                        <td>
                          {item.shape}
                        </td>

                        {/* ================= MRP ================= */}

                        <td>
                          {item.mrp}
                        </td>

                        {/* ================= STOCK ================= */}

                        <td className="text-center">
                          {item.avail_stock}
                        </td>

                        {/* =================================================
                            QUANTITY
                        ================================================= */}

                        <td className="text-center">

                          {!customQty[item.product_id] ? (

                            /* ================= DROPDOWN ================= */

                            <select
                              className="form-control form-control-sm text-center select-List"
                              value={
                                quantities[
                                  item.product_id
                                ] ?? 0
                              }
                              style={{
                                width: "100%",
                                fontSize: "13px",
                              }}
                              onChange={(e) => {
                                const value =
                                  e.target.value;

                                /* ============================
                                   10+ SELECT
                                ============================ */

                                if (
                                  value === "10+"
                                ) {
                                  setCustomQty(
                                    (prev) => ({
                                      ...prev,
                                      [item.product_id]:
                                        true,
                                    })
                                  );

                                  setQuantities(
                                    (prev) => ({
                                      ...prev,
                                      [item.product_id]:
                                        10,
                                    })
                                  );

                                  setPendingQuantities(
                                    (prev) => ({
                                      ...prev,
                                      [item.product_id]:
                                        10,
                                    })
                                  );

                                  return;
                                }

                                /* ============================
                                   1-9 SELECT
                                ============================ */

                                const qty =
                                  Number(value);

                                // IMPORTANT:
                                // Always remain dropdown mode
                                setCustomQty(
                                  (prev) => ({
                                    ...prev,
                                    [item.product_id]:
                                      false,
                                  })
                                );

                                setQuantities(
                                  (prev) => ({
                                    ...prev,
                                    [item.product_id]:
                                      qty,
                                  })
                                );

                                // Clear pending custom update
                                setPendingQuantities(
                                  (prev) => {
                                    const updated = {
                                      ...prev,
                                    };

                                    delete updated[
                                      item.product_id
                                    ];

                                    return updated;
                                  }
                                );

                                if (
                                  qty >= 1 &&
                                  qty <= 9
                                ) {
                                  handleQuantityChange(
                                    item,
                                    qty
                                  );
                                }
                              }}
                            >
                              {/* ============================
                                  0 TO 9
                              ============================ */}

                              {Array.from(
                                {
                                  length:
                                    Math.min(
                                      10,
                                      Number(
                                        item.avail_stock
                                      ) + 1
                                    ),
                                },
                                (_, i) => i
                              ).map((qty) => (
                                <option
                                  key={qty}
                                  value={qty}
                                >
                                  {qty}
                                </option>
                              ))}

                              {/* ============================
                                  10+
                              ============================ */}

                              {Number(
                                item.avail_stock
                              ) >= 10 && (
                                <option value="10+">
                                  10+
                                </option>
                              )}
                            </select>

                          ) : (

                            /* ================= CUSTOM INPUT ================= */

                            <input
                              type="text"
                              inputMode="numeric"
                              className="form-control form-control-sm text-center select-List"
                              value={
                                quantities[
                                  item.product_id
                                ] ?? ""
                              }
                              autoFocus
                              style={{
                                width: "100%",
                                fontSize: "13px",
                              }}
                              onFocus={(e) =>
                                e.target.select()
                              }
                              onChange={(e) =>
                                handleCustomQuantityChange(
                                  item,
                                  e.target.value
                                )
                              }
                              onKeyDown={(e) => {
                                if (
                                  e.key === "Enter"
                                ) {
                                  e.target.blur();
                                }
                              }}
                              onBlur={() => {
                                const value =
                                  quantities[
                                    item.product_id
                                  ];

                                if (
                                  value === "" ||
                                  value === undefined
                                ) {
                                  setQuantities(
                                    (prev) => ({
                                      ...prev,
                                      [item.product_id]:
                                        10,
                                    })
                                  );

                                  setPendingQuantities(
                                    (prev) => ({
                                      ...prev,
                                      [item.product_id]:
                                        10,
                                    })
                                  );
                                }
                              }}
                            />

                          )}
                        </td>

                        {/* =================================================
                            CART
                        ================================================= */}

                        <td className="text-center">
                          {(() => {
                            const productId =
                              item.product_id;

                            const quantity =
                              pendingQuantities[
                                productId
                              ] ??
                              quantities[
                                productId
                              ];

                            const hasQuantity =
                              quantity !==
                                undefined &&
                              quantity !== null &&
                              quantity !== "" &&
                              Number(quantity) > 0;

                            const stock =
                              Number(
                                item.avail_stock ||
                                  0
                              );

                            const hasPendingUpdate =
                              pendingQuantities[
                                productId
                              ] !== undefined &&
                              pendingQuantities[
                                productId
                              ] !== null &&
                              pendingQuantities[
                                productId
                              ] !== "";

                            const isUpdating =
                              updateLoadingId ===
                              productId;

                            const isAdding =
                              loadingId ===
                              productId;

                            const isOutOfStockQuantity =
                              Number(
                                quantity || 0
                              ) > stock;

                            return (
                              <button
                                type="button"
                                className={`btn btn-sm addtocart ${
                                  hasPendingUpdate
                                    ? "btn-update"
                                    : activeCartButtons[
                                        productId
                                      ]
                                    ? "btn-success"
                                    : "btn-outline-secondary"
                                }`}
                                disabled={
                                  isAdding ||
                                  isUpdating ||
                                  stock <= 0 ||
                                  !hasQuantity ||
                                  isOutOfStockQuantity
                                }
                                onClick={async () => {
                                  if (!hasQuantity) {
                                    toast.error(
                                      "Please select quantity"
                                    );

                                    return;
                                  }

                                  if (
                                    Number(quantity) >
                                    stock
                                  ) {
                                    toast.error(
                                      `Only ${stock} stock available`
                                    );

                                    return;
                                  }

                                  if (
                                    hasPendingUpdate
                                  ) {
                                    await handleUpdateQuantity(
                                      item
                                    );
                                  } else {
                                    await handleAddToCart(
                                      item
                                    );
                                  }
                                }}
                              >
                                {isAdding ||
                                isUpdating ? (
                                  <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                  />
                                ) : hasPendingUpdate ? (
                                  <i className="bi bi-arrow-repeat"></i>
                                ) : (
                                  <i className="bi bi-cart-plus"></i>
                                )}
                              </button>
                            );
                          })()}
                        </td>

                        {/* =================================================
                            WISHLIST
                        ================================================= */}

                        <td className="text-center">
                          <button
                            type="button"
                            className={`btn WishListbtn ${
                              activeWishlistButtons[
                                item.product_id
                              ]
                                ? "btn-danger"
                                : "btn-outline-secondary"
                            }`}
                            disabled={
                              wishlistLoadingId ===
                              item.product_id
                            }
                            onClick={() =>
                              handleAddWishlist(item)
                            }
                          >
                            {wishlistLoadingId ===
                            item.product_id ? (
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                              />
                            ) : (
                              <i
                                className={`bi ${
                                  activeWishlistButtons[
                                    item.product_id
                                  ]
                                    ? "bi-heart-fill"
                                    : "bi-heart"
                                }`}
                              ></i>
                            )}
                          </button>
                        </td>

                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="8"
                        className="text-center py-3"
                      >
                        No sizes available
                      </td>
                    </tr>
                  )}

                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* =========================================================
          CUSTOMER MODAL
      ========================================================= */}

      <Modal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setSearch("");
          setCustomers([]);
          setSelectedProduct(null);
          setActiveIndex(-1);
        }}
        className="model001"
        style={{
          background: "#0000008a",
        }}
      >
        <Modal.Body>

          <div className="position-relative serchlistdata pt-2">

            <input
              type="search"
              className="form-control"
              placeholder="Search Customer..."
              autoFocus
              value={search}
              style={{
                borderRadius: "5px",
                fontSize: "13px",
                height: "30px",
                marginTop: "0",
                width: "75%",
              }}
              onChange={(e) => {
                setSearch(e.target.value);
                setActiveIndex(-1);
              }}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") {
                  e.preventDefault();

                  setActiveIndex((prev) =>
                    prev <
                    filteredCustomers.length - 1
                      ? prev + 1
                      : 0
                  );
                }

                if (e.key === "ArrowUp") {
                  e.preventDefault();

                  setActiveIndex((prev) =>
                    prev > 0
                      ? prev - 1
                      : filteredCustomers.length - 1
                  );
                }

                if (
                  e.key === "Enter" &&
                  activeIndex >= 0 &&
                  filteredCustomers[activeIndex]
                ) {
                  e.preventDefault();

                  handleCustomerSelect(
                    filteredCustomers[
                      activeIndex
                    ]
                  );
                }
              }}
            />

            {search && (
              <div
                className="position-absolute"
                style={{
                  top: "calc(100% - 1px)",
                  left: 0,
                  right: 0,
                  maxHeight: "250px",
                  overflowY: "auto",
                  background: "#fff",
                  borderWidth:
                    "medium 1px 1px",
                  borderStyle:
                    "none solid solid",
                  borderColor:
                    "currentcolor rgb(222, 226, 230) rgb(222, 226, 230)",
                  borderRadius:
                    "4px 3px 6px 6px",
                  boxShadow:
                    "rgba(0, 0, 0, 0.15) 0px 15px 12px",
                  zIndex: 9999,
                  width: "75%",
                  fontSize: "13px",
                }}
              >

                {filteredCustomers.length > 0 ? (
                  filteredCustomers.map(
                    (customer, index) => (
                      <div
                        key={
                          customer.id ||
                          customer.customer_id ||
                          `customer-${index}`
                        }
                        onClick={() =>
                          handleCustomerSelect(
                            customer
                          )
                        }
                        onMouseEnter={() =>
                          setActiveIndex(index)
                        }
                        style={{
                          padding: "5px 12px",
                          cursor: "pointer",
                          background:
                            activeIndex === index
                              ? "#ededed"
                              : "#fff",
                          borderBottom:
                            "1px solid #ddd",
                        }}
                      >
                        {highlightText(
                          customer.accountName,
                          search
                        )}
                      </div>
                    )
                  )
                ) : (
                  <div
                    style={{
                      padding: 10,
                      textAlign: "center",
                    }}
                  >
                    No customer found
                  </div>
                )}

              </div>
            )}

          </div>

        </Modal.Body>
      </Modal>
    </>
  );
}

export default AddCart;
